import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { CommonState } from "@obg/ngx.common";
import {
	AUTH_MESSAGE_CONTEXT,
	CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT,
	CustomerManagementAction,
	MessageService,
	MessageType,
	SESSION_LOGIN_EXPIRED_CODES,
	SessionSelector,
} from "@obg/ngx.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { RouteSelector } from "@obg/ngx.router";
import { combineLatest, merge, Observable, Subscription } from "rxjs";
import { filter, take, tap } from "rxjs/operators";

@Component({
	selector: "obg-m-latest-privay-policy",
	templateUrl: "./entity-legal-notice.container.html",
	styleUrls: ["./entity-legal-notice.container.scss"],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityLegalNoticeContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-latest-privacy-policy") hostCssClass = true;

	@Output() close = new EventEmitter<void>();
	@Output() busy = new EventEmitter<boolean>();

	showError = false;
	legalNoticeTranslationKey: string | undefined;

	readonly isDesktop = this.deviceConfigService.isDesktop();

	private shouldCloseOnNavigation$: Observable<boolean>;
	private token: string | undefined;
	private data$$: Subscription | undefined;

	constructor(
		private readonly store: Store<CommonState>,
		private readonly messageService: MessageService,
		private readonly sessionSelector: SessionSelector,
		private readonly changeDetector: ChangeDetectorRef,
		private readonly deviceConfigService: DeviceConfigService,
		private readonly customerManagementAction: CustomerManagementAction
	) {
		this.shouldCloseOnNavigation$ = this.store.select(RouteSelector.isNavigating).pipe(
			filter((isNavigating) => isNavigating),
			take(1),
			tap(() => {
				this.close.emit();
			})
		);
	}

	ngOnInit(): void {
		const entityNoticeData$ = combineLatest([
			this.store.select(this.sessionSelector.getEntityLegalNoticeToken()),
			this.store.select(this.sessionSelector.getEntityLegalNoticeBodyKey()),
		]).pipe(
			tap(([token, legalNoticeTranslationKey]) => {
				this.token = token;
				this.legalNoticeTranslationKey = legalNoticeTranslationKey;
			})
		);

		const acceptError$ = this.messageService.consume$({
			context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.entityLegalNotice,
			messageType: MessageType.Error,
		});

		const loginError$ = this.messageService.consume$({
			context: [
				AUTH_MESSAGE_CONTEXT.login,
				AUTH_MESSAGE_CONTEXT.loginWithToken,
				AUTH_MESSAGE_CONTEXT.loginWithPersonalNumber,
			],
			messageType: MessageType.Error,
			excludeCodes: SESSION_LOGIN_EXPIRED_CODES,
		});

		const acceptSuccess$ = this.messageService
			.consume$({
				context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.entityLegalNotice,
				messageType: MessageType.Success,
			})
			.pipe(tap(() => this.close.emit()));

		const errors$ = merge(acceptError$, loginError$).pipe(
			tap(() => {
				this.busy.emit(false);
				this.showError = true;
			})
		);

		this.data$$ = merge(entityNoticeData$, errors$, acceptSuccess$, this.shouldCloseOnNavigation$).subscribe(() =>
			this.changeDetector.markForCheck()
		);
	}

	ngOnDestroy(): void {
		this.data$$?.unsubscribe();
	}

	accept(): void {
		if (!this.token) {
			return;
		}

		this.store.dispatch(this.customerManagementAction.acceptEntityLegalNotice(this.token));
		this.close.emit();
	}
}
